/**
 * src/components/ledger/QuickExpensePicker.tsx
 * Ultra-slick horizontal scrollable tray for 1-tap quick transaction selection
 */

import React, { useState, useEffect } from 'react';
import { Sparkles, History, Zap } from 'lucide-react';
import { 
  DEFAULT_EXPENSE_PRESETS, 
  getRecentPresets, 
  type ExpensePreset 
} from '../../utils/frequentExpenses';
import { haptic, playTapSound } from '../../utils/audioHaptics';

interface QuickExpensePickerProps {
  onSelect: (preset: ExpensePreset) => void;
  selectedDescription?: string;
  selectedAmount?: string;
}

export const QuickExpensePicker: React.FC<QuickExpensePickerProps> = ({
  onSelect,
  selectedDescription,
  selectedAmount
}) => {
  const [recents, setRecents] = useState<ExpensePreset[]>([]);

  useEffect(() => {
    setRecents(getRecentPresets());
  }, []);

  const handleChipClick = (preset: ExpensePreset) => {
    haptic.tap();
    playTapSound();
    onSelect(preset);
  };

  const combinedPresets = [
    ...recents.map(r => ({ ...r, isRecent: true })),
    ...DEFAULT_EXPENSE_PRESETS.filter(
      def => !recents.some(r => r.description.toLowerCase() === def.description.toLowerCase() && r.amount === def.amount)
    ).map(d => ({ ...d, isRecent: false }))
  ];

  return (
    <div style={{ marginBottom: '4px' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '6px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          fontSize: '0.72rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--text-tertiary)'
        }}>
          <Zap size={12} color="var(--ios-orange)" />
          <span>Quick Select Recent & Frequent</span>
        </div>
        <span style={{ fontSize: '0.6875rem', color: 'var(--text-tertiary)' }}>
          1-Tap Fill
        </span>
      </div>

      {/* Horizontal Scrollable Capsule Row */}
      <div
        className="hide-scrollbar"
        style={{
          display: 'flex',
          gap: '7px',
          overflowX: 'auto',
          paddingBottom: '4px',
          WebkitOverflowScrolling: 'touch',
          scrollSnapType: 'x mandatory'
        }}
      >
        {combinedPresets.map((item, idx) => {
          const isSelected =
            selectedDescription?.trim().toLowerCase() === item.description.trim().toLowerCase() &&
            Number(selectedAmount) === item.amount;

          return (
            <button
              key={`${item.description}_${item.amount}_${idx}`}
              type="button"
              onClick={() => handleChipClick(item)}
              className="ios-tap-target"
              style={{
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '5px 11px',
                borderRadius: 'var(--radius-pill)',
                background: isSelected
                  ? 'var(--accent-primary)'
                  : item.isRecent
                    ? 'rgba(255, 159, 10, 0.12)'
                    : 'var(--surface-glass)',
                border: isSelected
                  ? '1px solid var(--accent-primary)'
                  : item.isRecent
                    ? '1px solid rgba(255, 159, 10, 0.35)'
                    : 'var(--border-hairline)',
                color: isSelected
                  ? '#ffffff'
                  : item.isRecent
                    ? 'var(--ios-orange)'
                    : 'var(--text-primary)',
                fontSize: '0.78rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                scrollSnapAlign: 'start'
              }}
            >
              <span>{item.icon || (item.isRecent ? '⚡' : '📌')}</span>
              <span>{item.label || item.description}</span>
              <span style={{
                fontSize: '0.72rem',
                fontWeight: 800,
                opacity: 0.88,
                marginLeft: '1px'
              }}>
                ₹{item.amount}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
